<template>
  <div class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        {{ activeTab === "logs" ? "Access Logs" : "Access Attempts" }} -
        {{ totalCount }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="flex space-x-2">
        <button
          @click="setActiveTab('logs')"
          :class="[
            'btn',
            activeTab === 'logs'
              ? 'bg-gray-800 text-white'
              : 'bg-gray-200 text-gray-800',
          ]"
        >
          Access Logs
        </button>
        <button
          @click="setActiveTab('attempts')"
          :class="[
            'btn',
            activeTab === 'attempts'
              ? 'bg-gray-800 text-white dark:bg-gray-100 dark:text-gray'
              : 'bg-gray-200 text-gray-800 dark:text-white dark:bg-gray-800',
          ]"
        >
          Access Attempts
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">
      Loading {{ activeTab === "logs" ? "access logs" : "access attempts" }}...
    </div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          :placeholderText="
            activeTab === 'logs'
              ? 'Search access logs...'
              : 'Search access attempts...'
          "
          @input="debouncedFetchData"
        />
      </div>

      <!-- Date Range Filter -->
      <div class="flex items-center space-x-2">
        <input
          v-model="startDate"
          type="date"
          class="border border-gray-300 dark:border-gray-700 dark:bg-gray-700 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="fetchData"
        />
        <span>to</span>
        <input
          v-model="endDate"
          type="date"
          class="border border-gray-300 dark:border-gray-700 dark:bg-gray-700 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="fetchData"
        />
      </div>
    </div>

    <!-- Data Table -->
    <GenericTable
      v-if="!isLoading && !error"
      :columns="columns"
      :items="data"
      itemKey="id"
      :selectable="false"
      @rowClick="showDetails"
      @sort="sortData"
    >
      <!-- Custom cell templates -->
      <template #attempt_time="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #failures_since_start="{ value }">
        <span :class="getFailuresClass(value)">{{ value }}</span>
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      v-if="!isLoading && !error"
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="!hasPrevious"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="!hasNext"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          @change="updatePagination"
          :options="[{ id: 10, name: '10 per page' }, { id: 20, name: '20 per page' }, { id: 50, name: '50 per page' }, { id: 100, name: '100 per page' }]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import {
  getAccessLogsWithPagination,
  getAccessAttemptsWithPagination,
} from "@/services/accessLogService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useRouter } from "vue-router";
import debounce from "lodash/debounce";

export default {
  name: "AccessLogView",
  components: {
    SearchBar,
    GenericTable,
    GlassSelect,
  },
  setup() {
    const router = useRouter();
    const data = ref([]);
    const searchQuery = ref("");
    const startDate = ref("");
    const endDate = ref("");
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const error = ref(null);
    const isLoading = ref(false);
    const sortKey = ref("attempt_time");
    const sortOrder = ref("desc");
    const totalCount = ref(0);
    const hasNext = ref(false);
    const hasPrevious = ref(false);
    const activeTab = ref("logs");

    const columnsLogs = [
      { key: "attempt_time", label: "Timestamp", sortable: true, width: "20%" },
      { key: "username", label: "Username", sortable: true, width: "15%" },
      { key: "ip_address", label: "IP Address", sortable: true, width: "15%" },
      { key: "path_info", label: "Path", sortable: true, width: "30%" },
      { key: "user_agent", label: "User Agent", sortable: true, width: "20%" },
    ];

    const columnsAttempts = [
      { key: "attempt_time", label: "Timestamp", sortable: true, width: "20%" },
      { key: "username", label: "Username", sortable: true, width: "15%" },
      { key: "ip_address", label: "IP Address", sortable: true, width: "15%" },
      { key: "path_info", label: "Path", sortable: true, width: "20%" },
      {
        key: "failures_since_start",
        label: "Failures",
        sortable: true,
        width: "10%",
      },
      { key: "user_agent", label: "User Agent", sortable: true, width: "20%" },
    ];

    const columns = computed(() =>
      activeTab.value === "logs" ? columnsLogs : columnsAttempts
    );

    const isFiltered = computed(() => {
      return (
        searchQuery.value !== "" ||
        startDate.value !== "" ||
        endDate.value !== ""
      );
    });

    const totalPages = computed(() => {
      return Math.ceil(totalCount.value / itemsPerPage.value);
    });

    const fetchData = async () => {
      isLoading.value = true;
      try {
        error.value = null;
        const fetchFunction =
          activeTab.value === "logs"
            ? getAccessLogsWithPagination
            : getAccessAttemptsWithPagination;
        const response = await fetchFunction({
          page: currentPage.value,
          pageSize: itemsPerPage.value,
          search: searchQuery.value,
          startDate: startDate.value,
          endDate: endDate.value,
          ordering: `${sortOrder.value === "desc" ? "-" : ""}${sortKey.value}`,
        });
        data.value = response.results;
        totalCount.value = response.count;
        hasNext.value = !!response.next;
        hasPrevious.value = !!response.previous;
      } catch (err) {
        console.error(`Error fetching ${activeTab.value}:`, err);
        error.value = `Failed to fetch ${activeTab.value}. Please try again later.`;
        data.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const debouncedFetchData = debounce(fetchData, 300);

    const showDetails = (item) => {
      router.push({
        name:
          activeTab.value === "logs"
            ? "AccessLogDetails"
            : "AccessAttemptDetails",
        params: { id: item.id },
      });
    };

    const changePage = (delta) => {
      currentPage.value += delta;
      fetchData();
    };

    const updatePagination = () => {
      currentPage.value = 1;
      fetchData();
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const getFailuresClass = (failures) => {
      if (failures === 0) return "text-green-600";
      if (failures < 5) return "text-yellow-600";
      return "text-red-600";
    };

    const sortData = (key) => {
      if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
      } else {
        sortKey.value = key;
        sortOrder.value = "asc";
      }
      fetchData();
    };

    const setActiveTab = (tab) => {
      activeTab.value = tab;
      currentPage.value = 1;
      sortKey.value = "attempt_time";
      sortOrder.value = "desc";
      fetchData();
    };

    watch([searchQuery, startDate, endDate], () => {
      currentPage.value = 1;
      debouncedFetchData();
    });

    onMounted(fetchData);

    return {
      data,
      searchQuery,
      startDate,
      endDate,
      currentPage,
      itemsPerPage,
      columns,
      isFiltered,
      totalPages,
      totalCount,
      error,
      isLoading,
      hasNext,
      hasPrevious,
      showDetails,
      changePage,
      updatePagination,
      formatDateTime,
      getFailuresClass,
      sortData,
      debouncedFetchData,
      activeTab,
      setActiveTab,
    };
  },
};
</script>
